import { Box, Stack } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { CountryBar } from "src/components/country-bar";
import { SplashScreen } from "src/components/loading-screen";
import { setFilterCountry, setFilterNonBrandOpportunity, setFilterTimestep } from "src/context/reducers/filter-settings/filterSlice";
import { RootState } from "src/context/store";
import { HEADER } from "src/layouts/config-layout";
import { format } from "date-fns";
import { useRegionsQuery } from "src/context/api/non-branded/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { regionsToCountryItems, useNonBrandCountries } from "src/sections/cluster-manager/use-nonbrand-countries";
import { defaultNonBrandMonthRange, monthRangeActions } from "src/utils/month-range-filters";
import { CountryTabLabel } from "../../explore/CountryTabLabel";
import { PAGE_PADDING_X } from "../../funnel/constants";
import SearchTermsGrid from "../data-grid/search-terms-grid";
import NbKpiCards from "../nb-kpi-cards";
import NbTrafficCharts from "../nb-traffic-charts";
import "../_style.scss";

export default function SearchTerms() {
    const [ready, setReady] = useState(false);
    const initialized = useRef(false);
    const dispatch = useDispatch();
    const filterStore = useSelector((state: RootState) => state.filters);
    // Disabled until the landing defaults are applied, otherwise it fires once with the previous page's dates.
    const { countries, isLoading: isLoadingCountries } = useNonBrandCountries(ready);

    const workspace = useCurrentWorkspace();
    // Fixed for the whole mount so the initial selection does not depend on the previous page's range.
    const defaults = useMemo(() => defaultNonBrandMonthRange(), []);
    const { data: initialRegions, isSuccess, isError } = useRegionsQuery(
        {
            workspace_id: workspace?.id as number,
            start_date: format(defaults.start, "yyyy-MM-dd"),
            end_date: format(defaults.end, "yyyy-MM-dd"),
            devices: filterStore.devices,
            countries: [],
            platforms: filterStore.platforms,
            search_terms: [],
        },
        { skip: !workspace?.id || ready }
    );

    // The page forces a monthly timestep; give the previous one back on unmount so other pages calling
    // setFilterDate without skipAlignment do not get their start snapped to the 1st of the month.
    // Read from the store in an effect declared before the landing init: on a remount (workspace switch)
    // the previous instance's cleanup has already restored the original timestep by then.
    const store = useStore<RootState>();
    useEffect(() => {
        const previousTimestep = store.getState().filters.timestep;
        return () => {
            dispatch(setFilterTimestep({ timestep: previousTimestep, skipAlignment: true }));
        };
    }, [store, dispatch]);

    // The opportunity pill only exists on this page: do not carry it over to the next visit.
    useEffect(() => () => {
        dispatch(setFilterNonBrandOpportunity(null));
    }, [dispatch]);

    // Page default on landing, applied once: last 6 complete months, monthly, compared with the 6 months before,
    // every country with Non Brand traffic selected (a full selection is "All" in the CountryBar).
    useEffect(() => {
        if (initialized.current || !(isSuccess || isError)) return;
        initialized.current = true;
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        monthRangeActions(defaults, timeZone).forEach((action) => dispatch(action));
        const names = isSuccess ? regionsToCountryItems(initialRegions?.data ?? []).map((c) => c.country_name) : [];
        dispatch(setFilterCountry(names));
        setReady(true);
    }, [isSuccess, isError, initialRegions, defaults, dispatch]);

    // After landing, keep "All" in sync with the CountryBar list (it follows the date range): a selection that
    // covered the previous full list becomes the new full list; a partial selection is left alone.
    const previousCountryNames = useRef<string[] | null>(null);
    const selectedCountries = filterStore.countries;
    useEffect(() => {
        if (!ready || isLoadingCountries) return;
        const names = countries.map((c) => c.country_name);
        const previous = previousCountryNames.current;
        previousCountryNames.current = names;
        if (previous === null || (previous.length === names.length && previous.every((n, i) => n === names[i]))) return;
        const selected = new Set(selectedCountries ?? []);
        if (previous.every((n) => selected.has(n))) dispatch(setFilterCountry(names));
    }, [ready, isLoadingCountries, countries, selectedCountries, dispatch]);

    // Wait for the defaults so the queries do not fire once with another page's filters.
    if (!ready) return <SplashScreen />;

    return (
        <Box px={PAGE_PADDING_X}>
            <CountryBar
                countries={countries}
                multiple
                selectedCountries={filterStore.countries ?? []}
                onCountriesChange={(next) => dispatch(setFilterCountry(next))}
                loading={isLoadingCountries}
                stickyTop={HEADER.H_DESKTOP * 3 + 6}
                backgroundColor="background.paper"
                topGap={60}
                reserveGapWhenHidden
                contentPaddingX={PAGE_PADDING_X}
                tabSpacing={12}
                renderCountryLabel={(c, isSelected) => <CountryTabLabel country={c} isSelected={isSelected} />}
            />
            <Stack spacing={3} sx={{ fontFamily: "Public Sans" }} mt={3} pb={3}>
                <NbKpiCards />
                <NbTrafficCharts />
                <SearchTermsGrid />
            </Stack>
        </Box>
    );
}
